/**
 * DispatchSidebar - 调度控制台左侧导航栏组件
 * 
 * 功能：
 * - 显示用户信息和角色
 * - 提供主要功能模块的导航
 * - 支持菜单项的展开/折叠
 * - 提供容器状态的快速筛选
 * 
 * 设计特点：
 * - 固定宽度设计，不占用主内容区域
 * - 支持树形菜单结构
 * - 响应式交互效果
 * 
 * @author Claude Code Assistant
 * @version 1.0
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import {
  Mail,
  MessageSquare,
  Container,
  Send,
  Truck,
  Car,
  Building,
  User,
  Users,
  UserCheck,
  Warehouse,
  Building2,
  DollarSign,
  Calculator,
  Bell,
  ChevronDown,
  ChevronRight,
  Anchor,
  LayoutDashboard,
  TrendingUp,
  BarChart3,
  Settings,
  Receipt,
  Package,
  LogOut,
} from 'lucide-react';
import { authAPI } from '../../services/api';
import { usePermissions } from '../../hooks/usePermissions';
import { ApprovalBell } from './ApprovalBell';
import { checkSidebarNavigationState } from '../../utils/navigationUtils';
import { displayNameOf, initialsOf } from '../../utils/displayName';

// 组件属性接口定义
interface DispatchSidebarProps {
  expandedMenus: {
    container: boolean     // 容器菜单是否展开
    driver: boolean        // 司机菜单是否展开
    equipment: boolean     // 设备菜单是否展开
    client: boolean        // 客户菜单是否展开
    billPayment: boolean   // 账单支付菜单是否展开
    dashboard: boolean     // Dashboard菜单是否展开
    counting: boolean      // Counting菜单是否展开
  }
  selectedStatus: string   // 当前选中的状态
  toggleMenu: (menu: "container" | "driver" | "equipment" | "client" | "billPayment" | "dashboard" | "counting") => void  // 菜单切换函数
  setSelectedStatus: (status: string) => void                       // 状态切换函数
}

/**
 * DispatchSidebar 组件实现
 * Collapsed state is managed internally to avoid parent re-renders.
 */
export const DispatchSidebar: React.FC<DispatchSidebarProps> = ({
  expandedMenus,
  toggleMenu,
  setSelectedStatus,
}) => {
  const navigate = useNavigate()
  const location = useLocation()
  const isNavigatingRef = useRef(false)
  const { hasPageAccess } = usePermissions()

  // Sidebar collapsed state — managed here to avoid parent re-renders
  const [isCollapsed, setIsCollapsed] = useState(checkSidebarNavigationState)
  const collapseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const onMouseEnter = useCallback(() => {
    if (collapseTimerRef.current) {
      clearTimeout(collapseTimerRef.current)
      collapseTimerRef.current = null
    }
    setIsCollapsed(false)
  }, [])

  const onMouseLeave = useCallback(() => {
    collapseTimerRef.current = setTimeout(() => {
      setIsCollapsed(true)
      collapseTimerRef.current = null
    }, 200)
  }, [])

  // Check if a path is currently active
  const isActive = (path: string) => location.pathname === path
  const isGroupActive = (paths: string[]) => paths.some(p => location.pathname.startsWith(p))

  // 从 localStorage 获取当前登录用户信息；监听 `user-updated` 事件，
  // 当 /auth/me 刷新 localStorage 后立刻 rerender（修复 company_name / role 显示陈旧的问题）
  type StoredUser = {
    username?: string
    email?: string
    role?: string
    company_name?: string
    first_name?: string
    last_name?: string
  } | null
  const readStoredUser = (): StoredUser => {
    try {
      const stored = localStorage.getItem('user')
      if (stored) return JSON.parse(stored)
    } catch { /* ignore */ }
    return null
  }
  const [currentUser, setCurrentUser] = useState<StoredUser>(readStoredUser)

  useEffect(() => {
    const handler = () => setCurrentUser(readStoredUser())
    window.addEventListener('user-updated', handler)
    return () => window.removeEventListener('user-updated', handler)
  }, [])

  const userEmail = currentUser?.email || ''
  // 显示名规则统一放在 utils/displayName（与后端同一套优先级）
  const userName = displayNameOf(currentUser)
  const userRole = currentUser?.role || ''
  const companyName = currentUser?.company_name || localStorage.getItem('company_name') || ''
  const userInitials = initialsOf(userName)
  
  // Custom navigation handler that prevents immediate collapse
  const handleNavigation = (path: string) => {
    // Set a flag in sessionStorage to indicate navigation is happening
    sessionStorage.setItem('isNavigating', 'true')
    sessionStorage.setItem('navigationTime', Date.now().toString())
    navigate(path)
  }
  
  // Custom mouse leave handler that checks navigation state
  const handleMouseLeave = useCallback(() => {
    if (!isNavigatingRef.current) {
      onMouseLeave()
    }
  }, [onMouseLeave])
  
  return (
    <div
      className={`${isCollapsed ? 'w-20' : 'w-64 xl:w-80'} h-full bg-white border-r border-slate-200 flex flex-col transition-[width] duration-300 ease-in-out overflow-hidden relative z-20`}
      onMouseEnter={onMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* 品牌 Logo */}
      <div
        className={`${isCollapsed ? 'py-3 px-2 justify-center' : 'py-3.5 px-5 justify-between'} flex items-center`}
      >
        <div className={`flex items-center ${isCollapsed ? '' : 'gap-2.5'}`}>
          <img
            src="/logo.png"
            alt="EAloom"
            className={`${isCollapsed ? 'w-9 h-9' : 'w-8 h-8'} rounded-lg flex-shrink-0`}
          />
          {!isCollapsed && (
            <span className="font-bold text-lg tracking-tight text-slate-800 select-none">
              EAloom
            </span>
          )}
        </div>
        {!isCollapsed && <ApprovalBell />}
      </div>

      {/* 用户信息区域 */}
      <div className={`${isCollapsed ? 'py-3 px-3' : 'pt-1 pb-4 px-5'} border-b border-slate-100`}>
        {isCollapsed ? (
          <div className="flex flex-col items-center">
            <div className="relative">
              <Avatar className="w-10 h-10 flex-shrink-0">
                <AvatarImage src="/placeholder.svg?height=50&width=50" />
                <AvatarFallback className="bg-[#DCEDC8] text-[#33691E] text-sm font-semibold">{userInitials}</AvatarFallback>
              </Avatar>
              <span className="absolute bottom-0 right-0 w-2.5 h-2.5 bg-emerald-500 border-2 border-white rounded-full" />
            </div>
          </div>
        ) : (
          <div className="flex items-start gap-3.5">
            <div className="relative flex-shrink-0 mt-0.5">
              <Avatar className="w-13 h-13" style={{ width: '3.25rem', height: '3.25rem' }}>
                <AvatarImage src="/placeholder.svg?height=50&width=50" />
                <AvatarFallback className="bg-[#DCEDC8] text-[#33691E] text-base font-semibold">{userInitials}</AvatarFallback>
              </Avatar>
              <span className="absolute bottom-0 right-0 w-3.5 h-3.5 bg-emerald-500 border-2 border-white rounded-full" />
            </div>
            <div className="min-w-0 flex-1 space-y-1">
              <h3 className="font-semibold text-slate-900 text-base truncate leading-tight">{userName}</h3>
              <div className="flex items-center gap-1.5 text-sm leading-tight">
                {companyName && (
                  <span className="font-medium text-[#558B2F] truncate">{companyName}</span>
                )}
                {companyName && userRole && <span className="text-slate-300">·</span>}
                {userRole && <span className="text-xs text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded capitalize">{userRole}</span>}
              </div>
              {userEmail && (
                <p className="text-xs text-slate-400 truncate leading-tight">{userEmail}</p>
              )}
            </div>
          </div>
        )}
      </div>

      {/* 主导航菜单 */}
      <nav className={`flex-1 ${isCollapsed ? 'p-2' : 'px-4 py-3'} space-y-1 overflow-y-auto`}>

        {/* ── 核心业务 ── */}
        {!isCollapsed && <div className="px-3 pt-0.5 pb-2 text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Overview</div>}

        {/* Dashboard */}
        {(hasPageAccess('financials') || hasPageAccess('executive')) && (
        <div className="space-y-0.5">
          <div
            className={`flex items-center ${isCollapsed ? 'justify-center px-2 py-3' : 'gap-3 px-3 py-2'} rounded-md cursor-pointer transition-all duration-200
              ${isGroupActive(['/dispatch-console/financials', '/dispatch-console/executive'])
                ? 'text-[#00838F] bg-[#E0F7FA] font-medium'
                : 'text-slate-600 hover:bg-slate-50'}`}
            onClick={() => toggleMenu("dashboard")}
            title={isCollapsed ? "Dashboard" : ""}
          >
            <LayoutDashboard className={`${isCollapsed ? 'w-5 h-5' : 'w-4 h-4'}`} />
            {!isCollapsed && (
              <div className="flex items-center justify-between w-full">
                <span className="text-sm">Dashboard</span>
                {expandedMenus.dashboard ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
              </div>
            )}
          </div>
          {expandedMenus.dashboard && !isCollapsed && (
            <div className="ml-7 space-y-0.5">
              {hasPageAccess('financials') && (
              <button
                onClick={() => handleNavigation('/dispatch-console/financials')}
                className={`w-full text-left px-3 py-1.5 text-sm rounded-md transition-colors flex items-center gap-2
                  ${isActive('/dispatch-console/financials') ? 'text-[#00838F] bg-[#E0F7FA] font-medium' : 'text-slate-600 hover:bg-slate-50'}`}
              >
                <TrendingUp className="w-3 h-3" />
                Financials
              </button>
              )}
              {hasPageAccess('executive') && (
              <button
                onClick={() => handleNavigation('/dispatch-console/executive')}
                className={`w-full text-left px-3 py-1.5 text-sm rounded-md transition-colors flex items-center gap-2
                  ${isActive('/dispatch-console/executive') ? 'text-[#00838F] bg-[#E0F7FA] font-medium' : 'text-slate-600 hover:bg-slate-50'}`}
              >
                <BarChart3 className="w-3 h-3" />
                Executive
              </button>
              )}
            </div>
          )}
        </div>
        )}

        {/* Email */}
        {hasPageAccess('email') && (
        <div
          className={`flex items-center ${isCollapsed ? 'justify-center px-2 py-3' : 'gap-3 px-3 py-2'} rounded-md cursor-pointer transition-all duration-200
            ${isActive('/dispatch-console/email') ? 'text-[#00838F] bg-[#E0F7FA] font-medium' : 'text-slate-600 hover:bg-slate-50'}`}
          onClick={() => handleNavigation('/dispatch-console/email')}
          title={isCollapsed ? "Email" : ""}
        >
          <Mail className={`${isCollapsed ? 'w-5 h-5' : 'w-4 h-4'}`} />
          {!isCollapsed && <span className="text-sm">Email</span>}
        </div>
        )}

        {/* Messages */}
        {hasPageAccess('messages') && (
        <div
          className={`flex items-center ${isCollapsed ? 'justify-center px-2 py-3' : 'gap-3 px-3 py-2'} rounded-md cursor-pointer transition-all duration-200
            ${isActive('/dispatch-console/messages') ? 'text-[#00838F] bg-[#E0F7FA] font-medium' : 'text-slate-600 hover:bg-slate-50'}`}
          onClick={() => handleNavigation('/dispatch-console/messages')}
          title={isCollapsed ? "Messages" : ""}
        >
          <MessageSquare className={`${isCollapsed ? 'w-5 h-5' : 'w-4 h-4'}`} />
          {!isCollapsed && <span className="text-sm">Messages</span>}
        </div>
        )}

        {/* The reported-message queue lives under Settings → Reported Messages.
            It is a compliance obligation (App Store guideline 1.2), not part of
            the daily dispatch flow, so it is off the main rail. */}

        {/* ── 分隔线 + 运营管理 ── */}
        {!isCollapsed && <div className="border-t border-slate-100 my-2" />}
        {!isCollapsed && <div className="px-3 pt-1 pb-2 text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Operations</div>}

        {/* Container */}
        <div className="space-y-0.5">
          <div
            className={`flex items-center ${isCollapsed ? 'justify-center px-2 py-3' : 'gap-3 px-3 py-2'} rounded-md cursor-pointer transition-all duration-200
              ${isGroupActive(['/dispatch-console/tasks', '/dispatch-console/order-dispatch', '/dispatch-console/dispatch-tasks'])
                ? 'text-[#00838F] bg-[#E0F7FA] font-medium'
                : 'text-slate-600 hover:bg-slate-50'}`}
            onClick={() => toggleMenu("container")}
            title={isCollapsed ? "Container" : ""}
          >
            <Container className={`${isCollapsed ? 'w-5 h-5' : 'w-4 h-4'}`} />
            {!isCollapsed && (
              <div className="flex items-center justify-between w-full">
                <span className="text-sm">Container</span>
                {expandedMenus.container ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
              </div>
            )}
          </div>
          {expandedMenus.container && !isCollapsed && (
            <div className="ml-7 space-y-0.5">
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  navigate('/dispatch-console/tasks')
                  if (setSelectedStatus && typeof setSelectedStatus === 'function') {
                    setSelectedStatus("all")
                  }
                }}
                className={`w-full text-left px-3 py-1.5 text-sm rounded-md transition-colors flex items-center gap-2
                  ${isActive('/dispatch-console/tasks') ? 'text-[#00838F] bg-[#E0F7FA] font-medium' : 'text-slate-600 hover:bg-slate-50'}`}
              >
                <Container className="w-3 h-3" />
                Tasks
              </button>
              {hasPageAccess('order-dispatch') && (
              <button
                onClick={() => handleNavigation('/dispatch-console/order-dispatch')}
                className={`w-full text-left px-3 py-1.5 text-sm rounded-md transition-colors flex items-center gap-2
                  ${isActive('/dispatch-console/order-dispatch') ? 'text-[#00838F] bg-[#E0F7FA] font-medium' : 'text-slate-600 hover:bg-slate-50'}`}
              >
                <Package className="w-3 h-3" />
                Orders
              </button>
              )}
              {hasPageAccess('dispatch-tasks') && (
              <button
                onClick={() => handleNavigation('/dispatch-console/dispatch-tasks')}
                className={`w-full text-left px-3 py-1.5 text-sm rounded-md transition-colors flex items-center gap-2
                  ${isActive('/dispatch-console/dispatch-tasks') ? 'text-[#00838F] bg-[#E0F7FA] font-medium' : 'text-slate-600 hover:bg-slate-50'}`}
              >
                <Send className="w-3 h-3" />
                Dispatch
              </button>
              )}
            </div>
          )}
        </div>

        {/* Driver */}
        {hasPageAccess('driver-management') && (
        <div className="space-y-0.5">
          <div
            className={`flex items-center ${isCollapsed ? 'justify-center px-2 py-3' : 'gap-3 px-3 py-2'} rounded-md cursor-pointer transition-all duration-200
              ${isGroupActive(['/dispatch-console/driver-task', '/dispatch-console/driver-management'])
                ? 'text-[#00838F] bg-[#E0F7FA] font-medium'
                : 'text-slate-600 hover:bg-slate-50'}`}
            onClick={() => toggleMenu("driver")}
            title={isCollapsed ? "Driver" : ""}
          >
            <User className={`${isCollapsed ? 'w-5 h-5' : 'w-4 h-4'}`} />
            {!isCollapsed && (
              <div className="flex items-center justify-between w-full">
                <span className="text-sm">Driver</span>
                {expandedMenus.driver ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
              </div>
            )}
          </div>
          {expandedMenus.driver && !isCollapsed && (
            <div className="ml-7 space-y-0.5">
              <button
                onClick={() => handleNavigation('/dispatch-console/driver-task')}
                className={`w-full text-left px-3 py-1.5 text-sm rounded-md transition-colors flex items-center gap-2
                  ${isActive('/dispatch-console/driver-task') ? 'text-[#00838F] bg-[#E0F7FA] font-medium' : 'text-slate-600 hover:bg-slate-50'}`}
              >
                <Users className="w-3 h-3" />
                Driver List
              </button>
              <button
                onClick={() => handleNavigation('/dispatch-console/driver-management')}
                className={`w-full text-left px-3 py-1.5 text-sm rounded-md transition-colors flex items-center gap-2
                  ${isActive('/dispatch-console/driver-management') ? 'text-[#00838F] bg-[#E0F7FA] font-medium' : 'text-slate-600 hover:bg-slate-50'}`}
              >
                <UserCheck className="w-3 h-3" />
                Management
              </button>
            </div>
          )}
        </div>
        )}

        {/* Equipment */}
        {(hasPageAccess('truck-management') || hasPageAccess('chassis-management') || hasPageAccess('yard-management')) && (
        <div className="space-y-0.5">
          <div
            className={`flex items-center ${isCollapsed ? 'justify-center px-2 py-3' : 'gap-3 px-3 py-2'} rounded-md cursor-pointer transition-all duration-200
              ${isGroupActive(['/dispatch-console/truck-management', '/dispatch-console/chassis-management', '/dispatch-console/yard-management'])
                ? 'text-[#00838F] bg-[#E0F7FA] font-medium'
                : 'text-slate-600 hover:bg-slate-50'}`}
            onClick={() => toggleMenu("equipment")}
            title={isCollapsed ? "Equipment" : ""}
          >
            <Truck className={`${isCollapsed ? 'w-5 h-5' : 'w-4 h-4'}`} />
            {!isCollapsed && (
              <div className="flex items-center justify-between w-full">
                <span className="text-sm">Equipment</span>
                {expandedMenus.equipment ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
              </div>
            )}
          </div>
          {expandedMenus.equipment && !isCollapsed && (
            <div className="ml-7 space-y-0.5">
              {hasPageAccess('truck-management') && (
              <button
                onClick={() => handleNavigation('/dispatch-console/truck-management')}
                className={`w-full text-left px-3 py-1.5 text-sm rounded-md transition-colors flex items-center gap-2
                  ${isActive('/dispatch-console/truck-management') ? 'text-[#00838F] bg-[#E0F7FA] font-medium' : 'text-slate-600 hover:bg-slate-50'}`}
              >
                <Truck className="w-3 h-3" />
                Truck
              </button>
              )}
              {hasPageAccess('chassis-management') && (
              <button
                onClick={() => handleNavigation('/dispatch-console/chassis-management')}
                className={`w-full text-left px-3 py-1.5 text-sm rounded-md transition-colors flex items-center gap-2
                  ${isActive('/dispatch-console/chassis-management') ? 'text-[#00838F] bg-[#E0F7FA] font-medium' : 'text-slate-600 hover:bg-slate-50'}`}
              >
                <Car className="w-3 h-3" />
                Chassis
              </button>
              )}
              {hasPageAccess('yard-management') && (
              <button
                onClick={() => handleNavigation('/dispatch-console/yard-management')}
                className={`w-full text-left px-3 py-1.5 text-sm rounded-md transition-colors flex items-center gap-2
                  ${isActive('/dispatch-console/yard-management') ? 'text-[#00838F] bg-[#E0F7FA] font-medium' : 'text-slate-600 hover:bg-slate-50'}`}
              >
                <Building className="w-3 h-3" />
                Yard
              </button>
              )}
            </div>
          )}
        </div>
        )}

        {/* Partner */}
        {(hasPageAccess('client-management') || hasPageAccess('shipper') || hasPageAccess('port') || hasPageAccess('warehouse')) && (
        <div className="space-y-0.5">
          <div
            className={`flex items-center ${isCollapsed ? 'justify-center px-2 py-3' : 'gap-3 px-3 py-2'} rounded-md cursor-pointer transition-all duration-200
              ${isGroupActive(['/dispatch-console/client-management', '/dispatch-console/shipper', '/dispatch-console/port', '/dispatch-console/warehouse'])
                ? 'text-[#00838F] bg-[#E0F7FA] font-medium'
                : 'text-slate-600 hover:bg-slate-50'}`}
            onClick={() => toggleMenu("client")}
            title={isCollapsed ? "Partner" : ""}
          >
            <Users className={`${isCollapsed ? 'w-5 h-5' : 'w-4 h-4'}`} />
            {!isCollapsed && (
              <div className="flex items-center justify-between w-full">
                <span className="text-sm">Partner</span>
                {expandedMenus.client ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
              </div>
            )}
          </div>
          {expandedMenus.client && !isCollapsed && (
            <div className="ml-7 space-y-0.5">
              {hasPageAccess('client-management') && (
              <button
                onClick={() => handleNavigation('/dispatch-console/client-management')}
                className={`w-full text-left px-3 py-1.5 text-sm rounded-md transition-colors flex items-center gap-2
                  ${isActive('/dispatch-console/client-management') ? 'text-[#00838F] bg-[#E0F7FA] font-medium' : 'text-slate-600 hover:bg-slate-50'}`}
              >
                <UserCheck className="w-3 h-3" />
                Client
              </button>
              )}
              {hasPageAccess('shipper') && (
              <button
                onClick={() => handleNavigation('/dispatch-console/shipper')}
                className={`w-full text-left px-3 py-1.5 text-sm rounded-md transition-colors flex items-center gap-2
                  ${isActive('/dispatch-console/shipper') ? 'text-[#00838F] bg-[#E0F7FA] font-medium' : 'text-slate-600 hover:bg-slate-50'}`}
              >
                <Building2 className="w-3 h-3" />
                Shipper
              </button>
              )}
              {hasPageAccess('port') && (
              <button
                onClick={() => handleNavigation('/dispatch-console/port')}
                className={`w-full text-left px-3 py-1.5 text-sm rounded-md transition-colors flex items-center gap-2
                  ${isActive('/dispatch-console/port') ? 'text-[#00838F] bg-[#E0F7FA] font-medium' : 'text-slate-600 hover:bg-slate-50'}`}
              >
                <Anchor className="w-3 h-3" />
                Port
              </button>
              )}
              {hasPageAccess('warehouse') && (
              <button
                onClick={() => handleNavigation('/dispatch-console/warehouse')}
                className={`w-full text-left px-3 py-1.5 text-sm rounded-md transition-colors flex items-center gap-2
                  ${isActive('/dispatch-console/warehouse') ? 'text-[#00838F] bg-[#E0F7FA] font-medium' : 'text-slate-600 hover:bg-slate-50'}`}
              >
                <Warehouse className="w-3 h-3" />
                Warehouse
              </button>
              )}
            </div>
          )}
        </div>
        )}

        {/* ── 分隔线 + 财务 ── */}
        {!isCollapsed && <div className="border-t border-slate-100 my-2" />}
        {!isCollapsed && <div className="px-3 pt-1 pb-2 text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Finance</div>}

        {/* Bill & Payment */}
        {(hasPageAccess('driver-payment') || hasPageAccess('client-payment') || hasPageAccess('salary-model') || hasPageAccess('pricing-model')) && (
        <div className="space-y-0.5">
          <div
            className={`flex items-center ${isCollapsed ? 'justify-center px-2 py-3' : 'gap-3 px-3 py-2'} rounded-md cursor-pointer transition-all duration-200
              ${isGroupActive(['/dispatch-console/driver-payment', '/dispatch-console/client-payment', '/dispatch-console/salary-model', '/dispatch-console/pricing-model'])
                ? 'text-[#00838F] bg-[#E0F7FA] font-medium'
                : 'text-slate-600 hover:bg-slate-50'}`}
            onClick={() => toggleMenu("billPayment")}
            title={isCollapsed ? "Bill & Payment" : ""}
          >
            <DollarSign className={`${isCollapsed ? 'w-5 h-5' : 'w-4 h-4'}`} />
            {!isCollapsed && (
              <div className="flex items-center justify-between w-full">
                <span className="text-sm">Bill & Payment</span>
                {expandedMenus.billPayment ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
              </div>
            )}
          </div>
          {expandedMenus.billPayment && !isCollapsed && (
            <div className="ml-7 space-y-0.5">
              {hasPageAccess('driver-payment') && (
              <button
                onClick={() => handleNavigation('/dispatch-console/driver-payment')}
                className={`w-full text-left px-3 py-1.5 text-sm rounded-md transition-colors flex items-center gap-2
                  ${isActive('/dispatch-console/driver-payment') ? 'text-[#00838F] bg-[#E0F7FA] font-medium' : 'text-slate-600 hover:bg-slate-50'}`}
              >
                <DollarSign className="w-3 h-3" />
                Driver Payment
              </button>
              )}
              {hasPageAccess('client-payment') && (
              <button
                onClick={() => handleNavigation('/dispatch-console/client-payment')}
                className={`w-full text-left px-3 py-1.5 text-sm rounded-md transition-colors flex items-center gap-2
                  ${isActive('/dispatch-console/client-payment') ? 'text-[#00838F] bg-[#E0F7FA] font-medium' : 'text-slate-600 hover:bg-slate-50'}`}
              >
                <DollarSign className="w-3 h-3" />
                Client Payment
              </button>
              )}
              {hasPageAccess('salary-model') && (
              <button
                onClick={() => handleNavigation('/dispatch-console/salary-model')}
                className={`w-full text-left px-3 py-1.5 text-sm rounded-md transition-colors flex items-center gap-2
                  ${isActive('/dispatch-console/salary-model') ? 'text-[#00838F] bg-[#E0F7FA] font-medium' : 'text-slate-600 hover:bg-slate-50'}`}
              >
                <Calculator className="w-3 h-3" />
                Salary Model
              </button>
              )}
              {hasPageAccess('pricing-model') && (
              <button
                onClick={() => handleNavigation('/dispatch-console/pricing-model')}
                className={`w-full text-left px-3 py-1.5 text-sm rounded-md transition-colors flex items-center gap-2
                  ${isActive('/dispatch-console/pricing-model') ? 'text-[#00838F] bg-[#E0F7FA] font-medium' : 'text-slate-600 hover:bg-slate-50'}`}
              >
                <Calculator className="w-3 h-3" />
                Pricing Model
              </button>
              )}
            </div>
          )}
        </div>
        )}

        {/* Reminder */}
        {hasPageAccess('reminder') && (
        <div
          className={`flex items-center ${isCollapsed ? 'justify-center px-2 py-3' : 'gap-3 px-3 py-2'} rounded-md cursor-pointer transition-all duration-200
            ${isActive('/dispatch-console/reminder') ? 'text-[#00838F] bg-[#E0F7FA] font-medium' : 'text-slate-600 hover:bg-slate-50'}`}
          onClick={() => handleNavigation('/dispatch-console/reminder')}
          title={isCollapsed ? "Reminder" : ""}
        >
          <Bell className={`${isCollapsed ? 'w-5 h-5' : 'w-4 h-4'}`} />
          {!isCollapsed && <span className="text-sm">Reminder</span>}
        </div>
        )}

        {/* Counting */}
        {(hasPageAccess('invoice') || hasPageAccess('statement') || hasPageAccess('expense')) && (
        <div className="space-y-0.5">
          <div
            className={`flex items-center ${isCollapsed ? 'justify-center px-2 py-3' : 'gap-3 px-3 py-2'} rounded-md cursor-pointer transition-all duration-200
              ${isGroupActive(['/dispatch-console/counting'])
                ? 'text-[#00838F] bg-[#E0F7FA] font-medium'
                : 'text-slate-600 hover:bg-slate-50'}`}
            onClick={() => toggleMenu("counting")}
            title={isCollapsed ? "Counting" : ""}
          >
            <Calculator className={`${isCollapsed ? 'w-5 h-5' : 'w-4 h-4'}`} />
            {!isCollapsed && (
              <div className="flex items-center justify-between w-full">
                <span className="text-sm">Counting</span>
                {expandedMenus.counting ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
              </div>
            )}
          </div>
          {expandedMenus.counting && !isCollapsed && (
            <div className="ml-7 space-y-0.5">
              {hasPageAccess('invoice') && (
              <button
                onClick={() => handleNavigation('/dispatch-console/counting/invoice')}
                className={`w-full text-left px-3 py-1.5 text-sm rounded-md transition-colors flex items-center gap-2
                  ${isActive('/dispatch-console/counting/invoice') ? 'text-[#00838F] bg-[#E0F7FA] font-medium' : 'text-slate-600 hover:bg-slate-50'}`}
              >
                <Package className="w-3 h-3" />
                Invoice
              </button>
              )}
              {hasPageAccess('statement') && (
              <button
                onClick={() => handleNavigation('/dispatch-console/counting/statement')}
                className={`w-full text-left px-3 py-1.5 text-sm rounded-md transition-colors flex items-center gap-2
                  ${isActive('/dispatch-console/counting/statement') ? 'text-[#00838F] bg-[#E0F7FA] font-medium' : 'text-slate-600 hover:bg-slate-50'}`}
              >
                <Receipt className="w-3 h-3" />
                Statement
              </button>
              )}
              {hasPageAccess('expense') && (
              <button
                onClick={() => handleNavigation('/dispatch-console/counting/expense')}
                className={`w-full text-left px-3 py-1.5 text-sm rounded-md transition-colors flex items-center gap-2
                  ${isActive('/dispatch-console/counting/expense') ? 'text-[#00838F] bg-[#E0F7FA] font-medium' : 'text-slate-600 hover:bg-slate-50'}`}
              >
                <Receipt className="w-3 h-3" />
                Expense
              </button>
              )}
            </div>
          )}
        </div>
        )}
      </nav>

      {/* Settings & Logout - 固定底部 */}
      <div className={`${isCollapsed ? 'p-2' : 'p-4'} border-t border-slate-100 space-y-1`}>
        {hasPageAccess('settings') && (
        <div
          className={`flex items-center ${isCollapsed ? 'justify-center px-2 py-3' : 'gap-3 px-3 py-2'} rounded-md cursor-pointer transition-all duration-200
            ${isActive('/dispatch-console/settings') ? 'text-[#00838F] bg-[#E0F7FA] font-medium' : 'text-slate-600 hover:bg-slate-50'}`}
          onClick={() => handleNavigation('/dispatch-console/settings')}
          title={isCollapsed ? "Settings" : ""}
        >
          <Settings className={`${isCollapsed ? 'w-5 h-5' : 'w-4 h-4'}`} />
          {!isCollapsed && <span className="text-sm">Settings</span>}
        </div>
        )}
        <div
          className={`flex items-center ${isCollapsed ? 'justify-center px-2 py-3' : 'gap-3 px-3 py-2'} rounded-md cursor-pointer transition-all duration-200
            text-slate-400 hover:text-red-600 hover:bg-red-50`}
          onClick={() => authAPI.logout()}
          title={isCollapsed ? "Log out" : ""}
        >
          <LogOut className={`${isCollapsed ? 'w-5 h-5' : 'w-4 h-4'}`} />
          {!isCollapsed && <span className="text-sm">Log out</span>}
        </div>
      </div>
    </div>
  )
}